import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ShoppingBag, Menu, X, Zap, Dumbbell, Gift, Flame, Vote, Phone, User } from 'lucide-react';
import { ASSETS, TAGLINE, scrollToId } from '../lib/products';
import KrispButton from './KrispButton';

export const NAV_ITEMS = [
  { label: 'HOME', to: '/', icon: Zap, color: '#F7B500' },
  { label: 'SHOP', to: '/products', icon: Dumbbell, color: '#E11D24' },
  { label: 'VOTE', to: '/vote', icon: Vote, color: '#3E9B4F' },
  { label: 'GIFT', to: '/gifting', icon: Gift, color: '#E0632A' },
  { label: 'ABOUT', to: '/about', icon: Flame, color: '#F7B500' },
  { label: 'CONTACT', to: '/about#contact', icon: Phone, color: '#E11D24' },
];
const CURVE = [18, 8, 0, 0, 8, 18];
const STRIP = ['NOW LIVE · 50G · ₹50', '15G PROTEIN', 'ROASTED & PLANT BASED', 'NO PALM OIL', 'MULTIGRAIN', 'MIN ORDER 3 PACKETS', 'GO BEYOND'];

const isActive = (item, pathname, hash) => {
  if (item.to === '/about#contact') return pathname === '/about' && hash === '#contact';
  if (item.to === '/about') return pathname === '/about' && hash !== '#contact';
  if (item.to === '/products') return pathname.startsWith('/product');
  return pathname === item.to;
};

export default function Nav() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);

  const go = (to) => {
    setMenu(false);
    if (to === '/about#contact' && pathname === '/about') { scrollToId('contact'); window.history.replaceState(null, '', to); return; }
    navigate(to);
  };

  return (
    <>
      <header data-testid="main-nav" className="sticky top-0 z-[100] bg-ink text-cream shadow-[0_5px_0_#E11D24]">
        <div data-testid="power-strip" className="h-6 overflow-hidden bg-kyellow text-cocoa">
          <div className="marquee-track items-center h-full" style={{ '--mq-t': '30s' }}>
            {[0, 1].map((half) => (
              <div key={half} className="flex items-center shrink-0 h-full">
                {STRIP.map((t, i) => (
                  <span key={i} className="flex items-center gap-2 px-5 font-cond font-bold text-[11px] tracking-[0.25em] whitespace-nowrap">
                    <Zap size={11} className="fill-cocoa" /> {t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 h-[76px] flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo" data-cursor="expand" className="flex items-center shrink-0">
            <img src={ASSETS.logo} alt={`PROKRISP — ${TAGLINE}`} className="h-10 sm:h-12 object-contain" />
          </Link>

          <nav data-testid="chip-wheel-nav" className="hidden lg:block relative w-[620px] h-[68px] shrink-0 overflow-visible">
            <svg viewBox="0 0 620 64" className="absolute inset-0 w-full h-full" aria-hidden="true">
              <path d="M0 62 Q 310 -16 620 62" fill="none" stroke="rgba(245,243,238,0.18)" strokeWidth="2" strokeDasharray="6 8" />
            </svg>
            {NAV_ITEMS.map((item, i) => {
              const on = isActive(item, pathname, hash);
              const Icon = item.icon;
              return (
                <button key={item.label} data-testid={`nav-link-${item.label.toLowerCase()}`} data-cursor="plus" onClick={() => go(item.to)}
                  className="group absolute -translate-x-1/2 flex flex-col items-center gap-1 w-24"
                  style={{ left: `${(i / (NAV_ITEMS.length - 1)) * 100}%`, top: `${CURVE[i]}px` }}>
                  <motion.span
                    animate={on ? { scale: 1.2, y: -3 } : { scale: 1, y: 0 }}
                    whileHover={{ scale: 1.25, rotate: -10 }}
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${on ? 'border-kyellow bg-kyellow text-cocoa' : 'border-cream/30 text-cream/80 group-hover:border-cream'}`}>
                    <Icon size={15} strokeWidth={2.6} />
                  </motion.span>
                  <span className={`font-cond font-bold text-[12px] tracking-[0.2em] transition-colors duration-200 ${on ? 'text-kyellow' : 'text-cream/75 group-hover:text-cream'}`}>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-3 sm:gap-5">
            <button data-testid="nav-search" data-cursor="plus" aria-label="Search" onClick={() => go('/products')} className="text-cream/70 hover:text-kyellow transition-colors"><Search size={20} strokeWidth={2.4} /></button>
            <button data-testid="nav-account" aria-label="Account" onClick={() => go('/account')} className="text-cream/70 hover:text-kyellow transition-colors"><User size={20} strokeWidth={2.4} /></button>
            <button data-testid="nav-cart-button" data-cursor="plus" aria-label="Cart" onClick={() => go('/checkout')} className="relative text-cream/70 hover:text-kyellow transition-colors">
              <ShoppingBag size={20} strokeWidth={2.4} />
            </button>
            <span className="hidden md:block"><KrispButton testid="nav-shop-now" className="!py-2 !px-4 !text-xs" onClick={() => go('/products')}>SHOP NOW</KrispButton></span>
            <button data-testid="nav-menu-button" data-cursor="plus" aria-label="Menu" onClick={() => setMenu(true)} className="lg:hidden text-cream"><Menu size={24} /></button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menu && (
          <motion.div data-testid="mobile-menu"
            initial={{ clipPath: 'inset(0 0 100% 0)' }} animate={{ clipPath: 'inset(0 0 0% 0)' }} exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.45, ease: [0.83, 0, 0.17, 1] }}
            className="fixed inset-0 z-[300] bg-ink text-cream flex flex-col">
            <div className="h-16 px-5 flex items-center justify-between border-b border-kyellow/30">
              <img src={ASSETS.logo} alt="PROKRISP" className="h-9 object-contain" />
              <button data-testid="mobile-menu-close" onClick={() => setMenu(false)} aria-label="Close menu"><X size={24} /></button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-3">
              {[...NAV_ITEMS, { label: 'SOCIALS', to: '/socials', icon: Zap, color: '#E0632A' }, { label: 'ACCOUNT', to: '/account', icon: User, color: '#F7B500' }].map((l, i) => {
                const Icon = l.icon;
                return (
                  <motion.button key={l.label} data-testid={`mobile-link-${l.label.toLowerCase()}`}
                    initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.15 + i * 0.06 }}
                    onClick={() => go(l.to)} className="flex items-center gap-4 text-left font-display text-4xl text-cream hover:text-kyellow transition-colors duration-200">
                    <span className="w-10 h-10 rounded-full border-2 border-cream/30 flex items-center justify-center" style={{ color: l.color }}><Icon size={18} /></span>{l.label}
                  </motion.button>
                );
              })}
            </div>
            <p className="px-8 pb-10 font-cond text-xs tracking-[0.5em] text-kyellow">{TAGLINE}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
